import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Company } from '@interfaces/common/company';
import { Option } from '@interfaces/common/option';
import { AssignCompanySectorResponse } from '@interfaces/company/sectors/company-sector.response';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { CompanySectorHttpService } from '@services/http/company/company-sector-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { finalize, first } from 'rxjs/operators';

/** Valor da opção "Sem setor" (limpa o setor das empresas). */
const CLEAR_SECTOR = '__clear__';

/** Limite do backend por chamada da troca em lote. */
export const MAX_COMPANIES_PER_SECTOR_ASSIGNMENT = 500;

/**
 * Troca do setor padronizado de várias empresas de uma vez (card 3642), a partir da seleção da listagem de empresas.
 * Grava todas ou nenhuma (`PATCH companys/sector`): se alguma empresa saiu do acesso, nada muda e a modal mostra a
 * mensagem do backend.
 */
@Component({
  selector: 'app-company-sector-assign-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './company-sector-assign-modal.component.html',
  styleUrls: ['./company-sector-assign-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanySectorAssignModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** Setor gravado; a listagem recarrega e limpa a seleção. */
  @Output() sectorAssigned = new EventEmitter<AssignCompanySectorResponse>();

  private readonly sectorHttp = inject(CompanySectorHttpService);
  private readonly toast = inject(ToastHelperService);
  private readonly formErrorHandler = inject(FormErrorHandlerService);

  readonly maxCompanies = MAX_COMPANIES_PER_SECTOR_ASSIGNMENT;
  readonly companies = signal<Company[]>([]);
  readonly sectorOptions = signal<Option[]>([]);
  readonly selectedSector = signal<string | null>(null);
  readonly isLoadingSectors = signal(false);
  readonly isSaving = signal(false);

  readonly companyCount = computed(() => this.companies().length);
  readonly exceedsLimit = computed(() => this.companyCount() > this.maxCompanies);
  /** Até 5 nomes para a pessoa conferir o que vai mudar; o resto vira "e mais N". */
  readonly companyPreview = computed(() =>
    this.companies()
      .slice(0, 5)
      .map(company => company.displayName || company.tradeName || company.corporateName)
  );
  readonly remainingCount = computed(() => Math.max(this.companyCount() - 5, 0));
  readonly isFormValid = computed(
    () => !!this.selectedSector() && this.companyCount() > 0 && !this.exceedsLimit()
  );

  constructor() {
    super(inject(TranslateService));
  }

  /** Abre a modal para as empresas selecionadas na listagem. */
  open(companies: Company[]): void {
    this.companies.set(companies.filter(company => !!company.id));
    this.selectedSector.set(null);
    this.isSaving.set(false);
    this.loadSectors();
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'company-sector-assign-modal', false, true);
  }

  submitAction(): void {
    if (!this.isFormValid() || this.isSaving()) return;

    this.isSaving.set(true);
    const selected = this.selectedSector();

    this.sectorHttp
      .assignToCompanies({
        companyIds: this.companies().map(company => company.id!),
        companySectorId: selected === CLEAR_SECTOR ? null : selected,
      })
      .pipe(
        first(),
        finalize(() => this.isSaving.set(false))
      )
      .subscribe({
        next: result => {
          if (!result) return;
          const count = result.updatedCount;
          const noun = count === 1 ? 'empresa' : 'empresas';
          this.toast.showToastSuccess(
            result.companySectorName
              ? `Setor ${result.companySectorName} gravado em ${count} ${noun}`
              : `Setor removido de ${count} ${noun}`
          );
          this.sectorAssigned.emit(result);
          this.dialogRef?.close();
        },
        error: error => this.formErrorHandler.showToastErrors(error),
      });
  }

  private loadSectors(): void {
    this.isLoadingSectors.set(true);
    this.sectorHttp
      .listOptions()
      .pipe(
        first(),
        finalize(() => this.isLoadingSectors.set(false))
      )
      .subscribe({
        next: options =>
          this.sectorOptions.set([
            ...options,
            { label: 'Sem setor (limpar)', value: CLEAR_SECTOR },
          ]),
        error: error => this.formErrorHandler.showToastErrors(error),
      });
  }
}
